import { Observable } from 'rxjs';
import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { PaginatedResponse } from '../_models/paginatedResponse';
import { FieldActivity, FieldActivityGrouped } from '../_models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FieldActivityGroupedService {
  private http = inject(HttpClient);

  private apiBaseUrlV1 = environment.apiBaseUrl + 'api/private/v1';
  private apiBaseUrlV2 = environment.apiBaseUrl + 'api/private/v2';

  private lastPagedResult = signal<PaginatedResponse<FieldActivityGrouped> | null>(null);
  private lastItemResult = signal<FieldActivityGrouped | null>(null);

  lastPagedResultSignal = this.lastPagedResult.asReadonly();
  lastItemResultSignal = this.lastItemResult.asReadonly();

  getFieldActivitiesGroupedPaged(
    pageNumber: number,
    pageSize: number,
    year: number,
    donefilter: boolean | null,
    filterToApply = [],
    orderBy = null,
    textSearch = null
  ): Observable<PaginatedResponse<FieldActivityGrouped>> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected') ?? 'null');
    const companyId = companySelected?.id ?? null;

    const filtersList = [
      ...filterToApply,
      ...(donefilter != null ? [{ name: 'done', value: donefilter }] : []),
      { name: 'companyId', value: companyId },
      { name: 'year', value: year },
    ];

    const order = orderBy ?? [{ property: 'activityDate', direction: 'DESC' }];

    const payload = {
      number: pageNumber,
      max: pageSize,
      textSearch,
      sort: { orderBy: order },
      filter: { filters: filtersList },
    };

    return this.http
      .post<
        PaginatedResponse<FieldActivityGrouped>
      >(`${this.apiBaseUrlV2}/fieldActivityGroup`, payload)
      .pipe(
        tap((result) => this.lastPagedResult.set(result)),
        catchError((error: HttpErrorResponse) => throwError(() => error))
      );
  }

  getFieldActivityGroupedByUUID(uuid: string): Observable<FieldActivityGrouped> {
    const url = `${this.apiBaseUrlV1}/fieldActivityGrouped/${uuid}`;

    return this.http.get<FieldActivityGrouped>(url).pipe(
      tap((result) => this.lastItemResult.set(result)),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  cleanNonUsefulDataByFAGrouped(
    fieldActivityGroupedList: FieldActivityGrouped[]
  ): FieldActivityGrouped[] {
    fieldActivityGroupedList.forEach((grouped) => {
      grouped.activities.forEach((activity) => {
        // Clean mixes fieldActivity reference
        activity.mixes?.forEach((mix) => {
          if (mix?.fieldActivity?.id) {
            mix.fieldActivity = { id: mix.fieldActivity.id } as FieldActivity;
          }
        });

        // Clean fieldActivityItems fieldActivity reference
        if (activity.fieldActivityItems?.length) {
          activity.fieldActivityItems = activity.fieldActivityItems.map((item) => {
            if (item?.fieldActivity?.id) {
              return {
                ...item,
                fieldActivity: { id: item.fieldActivity.id } as FieldActivity,
              };
            }
            return item;
          });
        }

        // Remove machineActivity if present
        if (activity.machineActivity) {
          delete activity.machineActivity;
        }
      });
    });

    return fieldActivityGroupedList;
  }
}
